import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetKeysUpdatesStatsQuery,
  GetKeysUpdatesStatsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetKeysUpdatesStatsState
  extends MergeRequestState<GetKeysUpdatesStatsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getKeysUpdatesStats,
  isFetching: (state: ReduxState) =>
    state.server.getKeysUpdatesStats.isFetching,
  data: (state: ReduxState) => state.server.getKeysUpdatesStats.data,
  error: (state: ReduxState) => state.server.getKeysUpdatesStats.error,
};

const initialState: GetKeysUpdatesStatsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getKeysUpdatesStats';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetKeysUpdatesStatsQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getKeysUpdatesStats(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetKeysUpdatesStats {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getKeysUpdatesStats: ServerGetKeysUpdatesStats = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
